import React from 'react';
import {View, StyleSheet, TouchableOpacity, Linking, Image, Dimensions, ActivityIndicator} from 'react-native';
import {useTheme} from '../hooks/useTheme';

const SCREEN_WIDTH = Dimensions.get('window').width;
const AD_WIDTH = SCREEN_WIDTH - 24; // Full width minus margins

// Ad aspect ratios
const HEADER_ASPECT_RATIO = 320 / 50; // 6.4:1 for APP_HEADER (320x50)
const STANDARD_ASPECT_RATIO = 300 / 250; // 1.2:1 for MOBILE_HOME_P1-P6

interface AdBannerProps {
  position: 'top' | 'middle' | 'bottom';
  imageUrl?: string;
  targetUrl?: string;
  showPlaceholder?: boolean; // If true, shows empty placeholder while loading
}

export const AdBanner: React.FC<AdBannerProps> = ({
  position,
  imageUrl,
  targetUrl,
  showPlaceholder = false,
}) => {
  const {colors, isDark} = useTheme();

  const handlePress = () => {
    if (targetUrl) {
      Linking.openURL(targetUrl);
    }
  };

  // Calculate height based on position (top = header 300x150, others = 300x250)
  const aspectRatio = position === 'top' ? HEADER_ASPECT_RATIO : STANDARD_ASPECT_RATIO;
  const imageHeight = AD_WIDTH / aspectRatio;

  const dynamicStyles = {
    banner: {
      backgroundColor: colors.surfaceVariant,
    },
    image: {
      height: imageHeight,
    },
    placeholder: {
      height: imageHeight,
      backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
    },
  };

  // Show placeholder if no image but showPlaceholder is true
  if (!imageUrl) {
    if (showPlaceholder) {
      return (
        <View style={[styles.container, styles[`${position}Container`]]}>
          <View style={[styles.banner, styles.placeholder, dynamicStyles.placeholder]}>
            <ActivityIndicator size="small" color={colors.textSecondary} />
          </View>
        </View>
      );
    }
    return null;
  }

  return (
    <View style={[styles.container, styles[`${position}Container`]]}>
      <TouchableOpacity
        style={[styles.banner, dynamicStyles.banner]}
        onPress={handlePress}
        activeOpacity={targetUrl ? 0.8 : 1}
        disabled={!targetUrl}
      >
        <Image
          source={{uri: imageUrl}}
          style={[styles.image, dynamicStyles.image]}
          resizeMode="contain"
        />
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginHorizontal: 12,
    marginVertical: 8,
  },
  topContainer: {
    marginTop: 12,
    marginBottom: 4,
  },
  middleContainer: {
    marginVertical: 12,
  },
  bottomContainer: {
    marginTop: 4,
    marginBottom: 16,
  },
  banner: {
    borderRadius: 8,
    overflow: 'hidden',
  },
  placeholder: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  image: {
    width: '100%',
  },
});
